/**
 * useMyRequests — the signed-in person's OWN submissions across tools for the
 * Home "Your requests" card (Oct 2026, Falco's feedback): what is still
 * waiting for a decision, plus what was decided in the last 14 days.
 *
 * Expense statements, invoices, card statements, time sheets and absence
 * requests. Each tool is only queried when the user has it; the hooks accept
 * a null email and stay idle otherwise. Live snapshots, so a decision shows
 * up the moment it lands.
 */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { periodLabel, type Statement, type User as UserDoc } from '@daaily/shared';
import { useMyAbsences } from '../absence/hooks/useAbsence';
import { useMyInvoices } from '../invoices/hooks/useInvoices';
import { useMyCreditCardStatements } from '../creditCards/hooks/useCreditCards';
import { useMyTimeStatementsState } from '../timeTracking/lib';

export type MyRequestStatus = 'awaiting' | 'approved' | 'rejected';

export interface MyRequest {
  id: string;
  tool: string;
  label: string;
  status: MyRequestStatus;
  /** "Waiting for Alex" / "Approved 3 Oct" — one short line. */
  detail: string;
  link: string;
  /** Sort key (epoch ms): submission for awaiting, decision for decided. */
  at: number;
}

const RECENT_MS = 14 * 86_400_000;

const ms = (ts: unknown): number => {
  const t = ts as { toMillis?: () => number } | null | undefined;
  return t?.toMillis ? t.toMillis() : 0;
};
const fmtDay = (atMs: number) =>
  atMs ? new Date(atMs).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
const monthName = (year: number, month: number) =>
  new Date(Date.UTC(year, month - 1, 1)).toLocaleString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** Own expense statements (no shared hook exists; mirrors DashboardPage's query minus the ordering). */
function useMyExpenseStatements(email: string | null): Array<Statement & { id: string }> {
  const [rows, setRows] = useState<Array<Statement & { id: string }>>([]);
  useEffect(() => {
    if (!email) { setRows([]); return; }
    const q = query(collection(db, 'statements'), where('ownerEmail', '==', email));
    return onSnapshot(
      q,
      (snap) => setRows(snap.docs.map((d) => ({ ...(d.data() as Statement), id: d.id }))),
      () => setRows([]),
    );
  }, [email]);
  return rows;
}

export function useMyRequests(user: UserDoc | null): MyRequest[] {
  const email = user?.email.toLowerCase() ?? null;
  const tools = user?.tools ?? {};
  const absences = useMyAbsences(tools.absence?.enabled ? email : null);
  const expenses = useMyExpenseStatements(tools.expenses?.enabled ? email : null);
  const { invoices } = useMyInvoices(tools.invoices?.enabled ? email : null);
  const { statements: cards } = useMyCreditCardStatements(tools.creditCards?.enabled ? email : null);
  const { data: timeSheets } = useMyTimeStatementsState(
    tools.timeTracking?.enabled && user?.timeTracksOwn !== false ? email : null,
  );

  return useMemo(() => {
    const now = Date.now();
    const out: MyRequest[] = [];
    const recent = (at: number) => at > 0 && now - at <= RECENT_MS;
    const decided = (status: 'approved' | 'rejected', at: number) =>
      `${status === 'approved' ? 'Approved' : 'Sent back'} ${fmtDay(at)}`;

    for (const r of absences ?? []) {
      const when = r.startDate === r.endDate ? r.startDate : `${r.startDate} → ${r.endDate}`;
      const label = `${r.type} · ${when}`;
      if (r.status === 'submitted') {
        out.push({ id: `abs:${r.id}`, tool: 'Absence', label, status: 'awaiting', detail: 'Waiting for approval', link: '/absence', at: ms(r.submittedAt) });
      } else if ((r.status === 'approved' || r.status === 'rejected') && recent(ms(r.decisionAt))) {
        out.push({ id: `abs:${r.id}`, tool: 'Absence', label, status: r.status, detail: decided(r.status, ms(r.decisionAt)), link: '/absence', at: ms(r.decisionAt) });
      }
    }
    for (const s of expenses) {
      const label = `Expenses ${monthName(s.year, s.month)}`;
      const link = `/expenses/statements/${s.id}`;
      if (s.status === 'submitted' || s.status === 'pending_2nd_approval') {
        out.push({ id: `exp:${s.id}`, tool: 'Expenses', label, status: 'awaiting', detail: s.status === 'pending_2nd_approval' ? 'Waiting for the second approver' : 'Waiting for approval', link, at: ms(s.submittedAt) });
      } else if (s.status === 'approved' && recent(ms(s.approvedAt))) {
        out.push({ id: `exp:${s.id}`, tool: 'Expenses', label, status: 'approved', detail: decided('approved', ms(s.approvedAt)), link, at: ms(s.approvedAt) });
      } else if (s.status === 'rejected' && recent(ms(s.rejectedAt))) {
        out.push({ id: `exp:${s.id}`, tool: 'Expenses', label, status: 'rejected', detail: decided('rejected', ms(s.rejectedAt)), link, at: ms(s.rejectedAt) });
      }
    }
    for (const v of invoices) {
      const label = `Invoice ${v.invoiceNumber || ''} · ${v.amount.toLocaleString('en-GB', { minimumFractionDigits: 2 })} ${v.currency}`;
      const link = `/invoices/${v.id}`;
      if (v.status === 'submitted') {
        out.push({ id: `inv:${v.id}`, tool: 'Invoices', label, status: 'awaiting', detail: 'Waiting for approval', link, at: ms(v.updatedAt) });
      } else if (v.status === 'approved' && recent(ms(v.approvedAt))) {
        out.push({ id: `inv:${v.id}`, tool: 'Invoices', label, status: 'approved', detail: decided('approved', ms(v.approvedAt)), link, at: ms(v.approvedAt) });
      } else if (v.status === 'rejected' && recent(ms(v.rejectedAt))) {
        out.push({ id: `inv:${v.id}`, tool: 'Invoices', label, status: 'rejected', detail: decided('rejected', ms(v.rejectedAt)), link, at: ms(v.rejectedAt) });
      }
    }
    for (const c of cards) {
      const label = `Card statement ${c.periodLabel}`;
      const link = `/credit-cards/statements/${c.id}`;
      if (c.status === 'submitted') {
        out.push({ id: `cc:${c.id}`, tool: 'Credit Cards', label, status: 'awaiting', detail: 'Waiting for approval', link, at: ms(c.submittedAt) });
      } else if (c.status === 'approved' && recent(ms(c.approvedAt))) {
        out.push({ id: `cc:${c.id}`, tool: 'Credit Cards', label, status: 'approved', detail: decided('approved', ms(c.approvedAt)), link, at: ms(c.approvedAt) });
      } else if (c.status === 'rejected' && recent(ms(c.rejectedAt))) {
        out.push({ id: `cc:${c.id}`, tool: 'Credit Cards', label, status: 'rejected', detail: decided('rejected', ms(c.rejectedAt)), link, at: ms(c.rejectedAt) });
      }
    }
    for (const t of timeSheets.values()) {
      const label = `Time sheet ${periodLabel(t.period)}`;
      if (t.status === 'submitted') {
        out.push({ id: `time:${t.period}`, tool: 'Time Tracking', label, status: 'awaiting', detail: 'Waiting for approval', link: '/time', at: ms(t.submittedAt) });
      } else if ((t.status === 'approved' || t.status === 'rejected') && recent(ms(t.decidedAt))) {
        out.push({ id: `time:${t.period}`, tool: 'Time Tracking', label, status: t.status, detail: decided(t.status, ms(t.decidedAt)), link: '/time', at: ms(t.decidedAt) });
      }
    }
    // Waiting first (newest submission on top), then the recent decisions.
    out.sort((a, b) => (a.status === 'awaiting') === (b.status === 'awaiting') ? b.at - a.at : a.status === 'awaiting' ? -1 : 1);
    return out;
  }, [absences, expenses, invoices, cards, timeSheets]);
}
